import { Box, Card, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import {
  DeleteRCNContract,
  DeleteStartContract,
  GetBudgetSummary,
  GetContract, AcceptContract,
  SubmitContract,
  UploadRCNContract,
  UploadStartContract
} from '../../../../services/AnnualPlanService'
import { type BudgetSummary } from '../../../../models/BudgetSummary'
import generatePDF, { Margin } from 'react-to-pdf'
import { useAnnualPlanUserRole } from '../../../../hooks/use-annualPlanUserRole'
import { AnnualPlanRole } from '../../../../models/AnnualPlanRole'
import { type PlanContract } from '../../../../models/PlanContract'
import { DeleteOutline, DownloadOutlined, TextSnippetOutlined, UploadOutlined } from '@mui/icons-material'
import { BlockButton } from '../../../customComponents/buttons/BlockButton'
import { DragAndDropFilePicker } from '../../../customComponents/DragAndDropFilePicker'
import { BlueButton } from '../../../customComponents/buttons/BlueButton'
import { PlanOrganizationTaskStatus } from '../../../../models/PlanOrganizationTask'
import { Modal } from '../../../customComponents/Modal'
import { GetFileDownloadUrl, GetFileName, GetFileUrl } from '../../../../utils/fileUtils'
import { toast } from 'react-toastify'
import { Comments } from '../../../customComponents/comments/Comments'
import { CommentParentType } from '../../../../models/Comment'
import { BudgetSummaryPreview } from './BudgetSummaryPreview'
import { handleApiResponse } from '../../../../utils/apiHelpers'

interface Props {
  annualPlanId: number
  rcnId: number
  organizationName: string
  showComments?: boolean
}

export const SignContractTab = (props: Props): JSX.Element => {
  const componentRef = useRef<HTMLDivElement>()
  const { userRole } = useAnnualPlanUserRole(props.annualPlanId)

  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null)
  const [planContract, setPlanContract] = useState<PlanContract | null>(null)
  const [budgetPdfUrl, setBudgetPdfUrl] = useState<string | null>(null)
  const [startPdfUrl, setStartPdfUrl] = useState<string | null>(null)
  const [rcnPdfUrl, setRcnPdfUrl] = useState<string | null>(null)
  const [displayedPdfUrl, setDisplayedPdfUrl] = useState<string | null>(null)
  const [isShowingUploadArea, setIsShowingUploadArea] = useState(false)
  const [fileName, setFileName] = useState<string | undefined>(undefined)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [isConfirmingRelease, setIsConfirmingRelease] = useState(false)
  const [isConfirmingFinalize, setIsConfirmingFinalize] = useState(false)

  const loadBudgetSummary = async (): Promise<void> => {
    const data = await handleApiResponse(GetBudgetSummary(props.annualPlanId))
    if (data != null) setBudgetSummary(data)
  }

  const loadContract = async (): Promise<void> => {
    const contract = await handleApiResponse(GetContract(props.annualPlanId))
    if (contract == null) return
    setPlanContract(contract)

    if (contract.rcnSignedContractKey != null) {
      const url = (GetFileUrl(contract.rcnSignedContractKey) ?? '') + '#toolbar=0'
      setRcnPdfUrl(url)

      // If the user is a Plan Admin, then set this as the file that they uploaded
      if (userRole === AnnualPlanRole.PlanAdministrator) {
        const name = GetFileName(contract.rcnSignedContractKey)
        setFileName(name)
      }
    }

    if (contract.startSignedContractKey != null) {
      const url = (GetFileUrl(contract.startSignedContractKey) ?? '') + '#toolbar=0'
      setStartPdfUrl(url)

      // If the user is a Plan Reviewer, then set this as the file that they uploaded
      if (userRole === AnnualPlanRole.PlanReviewer) {
        const name = GetFileName(contract.startSignedContractKey)
        setFileName(name)
      }
    }
  }

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      await Promise.all([
        loadBudgetSummary(),
        loadContract()
      ])
    }

    if (userRole != null) {
      void loadData()
    }
  }, [userRole])

  useEffect(() => {
    if (rcnPdfUrl != null) {
      setDisplayedPdfUrl(rcnPdfUrl)
    } else if (startPdfUrl != null) {
      setDisplayedPdfUrl(startPdfUrl)
    } else if (budgetPdfUrl != null) {
      setDisplayedPdfUrl(budgetPdfUrl)
    }
  }, [rcnPdfUrl, startPdfUrl, budgetPdfUrl])

  const generateBudgetPdf = async (node: HTMLDivElement | null): Promise<void> => {
    if (node == null) {
      return
    }

    componentRef.current = node
    const pdf = await generatePDF(componentRef, { method: 'build', page: { margin: Margin.MEDIUM, format: 'letter', orientation: 'portrait' } })
    const blob = new Blob([pdf.output('blob')], { type: 'application/pdf' })
    const blobUrl = URL.createObjectURL(blob) + '#toolbar=0'
    setBudgetPdfUrl(blobUrl)
  }

  const uploadFile = async (file: File | undefined): Promise<void> => {
    if (file == null || planContract == null) {
      return
    }

    setFileName(file.name)

    if (userRole === AnnualPlanRole.PlanReviewer) {
      const url = URL.createObjectURL(file) + '#toolbar=0'
      setStartPdfUrl(url)
      await UploadStartContract(planContract.planOrganizationTaskId, file)
    } else if (userRole === AnnualPlanRole.PlanAdministrator) {
      const url = URL.createObjectURL(file) + '#toolbar=0'
      setRcnPdfUrl(url)
      await UploadRCNContract(planContract.planOrganizationTaskId, file)
    }
  }

  const deleteFile = async (): Promise<void> => {
    setIsConfirmingDelete(true)
  }

  const confirmDeleteFile = async (): Promise<void> => {
    if (planContract == null) {
      return
    }

    setFileName(undefined)
    setIsConfirmingDelete(false)
    toast.success('The signed contract was deleted')

    if (userRole === AnnualPlanRole.PlanReviewer) {
      setStartPdfUrl(null)
      await DeleteStartContract(planContract.planOrganizationTaskId)
    } else if (userRole === AnnualPlanRole.PlanAdministrator) {
      setRcnPdfUrl(null)
      await DeleteRCNContract(planContract.planOrganizationTaskId)
    }
  }

  const releaseAndSendToRcn = (): void => {
    setIsConfirmingRelease(true)
  }

  const confirmReleaseAndSendToRcn = async (): Promise<void> => {
    if (planContract == null) return

    setIsConfirmingRelease(false)
    const { success, errors } = await SubmitContract(planContract.planOrganizationTaskId)
    if (!success) {
      if (errors.length > 0) errors.forEach((e) => toast.error(e))
      else toast.error('Something went wrong while submitting the contract')
      return
    }

    toast.success('The signed contract was released')
    await loadContract()
  }

  const finalizeAndSubmit = (): void => {
    setIsConfirmingFinalize(true)
  }

  const confirmSubmit = async (): Promise<void> => {
    if (planContract == null) {
      return
    }

    setIsConfirmingFinalize(false)

    const { success, errors } = await AcceptContract(planContract.planOrganizationTaskId)
    if (!success) {
      if (errors.length > 0) errors.forEach((e) => toast.error(e))
      else toast.error('Something went wrong while accepting the contract')
      return
    }

    toast.success('RCN contract submitted')
    await loadContract()
  }

  const fileHasBeenUploaded = fileName != null
  const selectedFileDownloadUrl = GetFileDownloadUrl(planContract?.rcnSignedContractKey ?? planContract?.startSignedContractKey ?? '')
  const canEdit = (userRole === AnnualPlanRole.PlanReviewer && planContract?.status === PlanOrganizationTaskStatus.Drafting) ||
    (userRole === AnnualPlanRole.PlanAdministrator && planContract?.status === PlanOrganizationTaskStatus.Submitted)

  if (budgetSummary == null || planContract == null) {
    return <CircularProgress data-testid='contractTabSpinner'/>
  }

  return <Card sx={{ bgcolor: 'var(--grey-100)', p: '1em', borderRadius: '16px' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant='h2'>
        Finalize Contract
      </Typography>

      <Comments
        parentType={CommentParentType.PlanOrganizationTask}
        parentId={planContract.planOrganizationTaskId}
        showComments={props.showComments}
      />
    </Box>

    <Typography variant='body1' sx={{ mb: '1em' }}>
      To complete the contracting process, download your RCN's Contract, have it signed by an authorized fiscal agent representative, and upload and submit the signed contract to START.
    </Typography>

    <Typography variant='h3' sx={{ mt: '1em', mb: '.5em' }}>
      {displayedPdfUrl === budgetPdfUrl &&
        'Exhibit Preview'
      }

      {!fileHasBeenUploaded && displayedPdfUrl === startPdfUrl &&
        'Contract Preview'
      }

      {fileHasBeenUploaded &&
        'Signed Contract Preview'
      }
    </Typography>

    {displayedPdfUrl != null &&
      <Box sx={{ display: 'flex' }} data-testid='contractPreviewContainer'>
        <iframe style={{ width: '850px', height: '950px' }} src={displayedPdfUrl} title='Contract Preview' />

        <Box sx={{ maxWidth: '340px', width: '-webkit-fill-available' }}>
          <Box sx={{ ml: '20px' }}>
            <Box sx={{ bgcolor: 'var(--blue-700)', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', p: '10px' }}>
              <Typography variant='h3' sx={{ color: 'white', textAlign: 'center' }}>Actions</Typography>
            </Box>

            <Stack
              gap='10px'
              direction='column'
              sx={{
                bgcolor: 'white',
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
                py: '30px',
                px: '20px'
              }}
            >
              {userRole === AnnualPlanRole.PlanReviewer &&
                <a href={budgetPdfUrl ?? ''} download={`exhibit_b_${props.organizationName}.pdf`} data-testid='downloadExhibitB'>
                  <BlockButton
                    onClick={() => {}}
                    startIcon={<DownloadOutlined />}
                    sx={{ justifyContent: 'left', width: '-webkit-fill-available', py: '12px', px: '24px' }}
                  >
                    Download Exhibit B
                  </BlockButton>
                </a>
              }

              {userRole === AnnualPlanRole.PlanAdministrator &&
                <a href={GetFileDownloadUrl(planContract?.startSignedContractKey)} download='START_signed_contract.pdf' data-testid='downloadStartContract'>
                  <BlockButton
                    onClick={() => {}}
                    startIcon={<DownloadOutlined />}
                    sx={{ justifyContent: 'left', width: '-webkit-fill-available', py: '12px', px: '24px' }}
                  >
                    Download START Signed Contract
                  </BlockButton>
                </a>
              }

              {userRole === AnnualPlanRole.PlanReviewer && planContract?.rcnSignedContractKey != null &&
                <a href={GetFileDownloadUrl(planContract?.rcnSignedContractKey)} download='START_countersigned_contract.pdf' data-testid='downloadCounterStartContract'>
                  <BlockButton
                    onClick={() => {}}
                    startIcon={<DownloadOutlined />}
                    sx={{ justifyContent: 'left', width: '-webkit-fill-available', py: '12px', px: '24px' }}
                  >
                    Download Countersigned Contract
                  </BlockButton>
                </a>
              }

              <BlockButton
                disabled={fileHasBeenUploaded}
                startIcon={<UploadOutlined />}
                onClick={() => { setIsShowingUploadArea(true) }}
                dataTestId='uploadSignedContract'
                backgroundColor={isShowingUploadArea ? 'var(--blue-50)' : 'white'}
                sx={{ justifyContent: 'left', width: '-webkit-fill-available', py: '12px', px: '24px' }}
              >
                Upload Signed Contract
              </BlockButton>

              {isShowingUploadArea && !fileHasBeenUploaded &&
                <DragAndDropFilePicker selectFile={uploadFile} accept='.pdf' />
              }

              {fileHasBeenUploaded &&
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <a href={selectedFileDownloadUrl} download='signed_contract.pdf' data-testid='uploadedFile' style={{ textDecoration: 'none' }}>
                    <Box sx={{ cursor: 'pointer', display: 'flex' }}>
                      <TextSnippetOutlined sx={{ color: 'var(--blue-500)' }} />
                      <Typography variant='body1' sx={{ ml: '.5em', color: 'black' }}>{fileName}</Typography>
                    </Box>
                  </a>

                  {canEdit &&
                    <DeleteOutline sx={{ cursor: 'pointer' }} onClick={deleteFile} data-testid='deleteFile' />
                  }
                </Box>
              }
            </Stack>
          </Box>

          {userRole === AnnualPlanRole.PlanReviewer && fileHasBeenUploaded && canEdit &&
            <BlueButton onClick={releaseAndSendToRcn} sx={{ ml: '20px', mt: '20px' }} dataTestId='releaseToRcn'>
              Release & Send to RCN
            </BlueButton>
          }

          {userRole === AnnualPlanRole.PlanAdministrator && fileHasBeenUploaded && canEdit &&
            <BlueButton onClick={finalizeAndSubmit} sx={{ ml: '20px', mt: '20px' }} dataTestId='finalizeAndSubmit'>
              Submit
            </BlueButton>
          }
        </Box>

        {isConfirmingDelete &&
          <Modal
            open={true}
            title='Delete File'
            confirmButtonText='Yes, Delete'
            cancelButtonText='No, Cancel'
            bodyContent='Are you sure you want to delete this file?'
            onClose={() => { setIsConfirmingDelete(false) }}
            onConfirm={confirmDeleteFile}
          />
        }

        {isConfirmingRelease &&
          <Modal
            open={true}
            title='Release & Send to RCN'
            confirmButtonText='Yes, Release'
            cancelButtonText='No, Cancel'
            bodyContent='Are you sure you want to release to the RCN? Once you have released the contract, the file cannot be changed and the RCN will be able to interact with the contract.'
            onClose={() => { setIsConfirmingRelease(false) }}
            onConfirm={confirmReleaseAndSendToRcn}
          />
        }

        {isConfirmingFinalize &&
          <Modal
            open={true}
            title='Submit'
            confirmButtonText='Yes, submit the Contract'
            cancelButtonText='No, do not submit the Contract'
            bodyContent='Are you sure you want to submit your signed RCN Contract to START? This is the final step in the process. All items will be locked and the signed contract will be shared with START.'
            onClose={() => { setIsConfirmingFinalize(false) }}
            onConfirm={confirmSubmit}
          />
        }
      </Box>
    }

    {budgetPdfUrl == null && userRole === AnnualPlanRole.PlanReviewer &&
      <Box ref={generateBudgetPdf} sx={{ bgcolor: 'white', px: '60px', py: '32px' }} data-testid='budgetSummary'>
        <BudgetSummaryPreview
          organizationName={props.organizationName}
          budgetSummary={budgetSummary}
        />
      </Box>
    }
  </Card>
}
